import { useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid, Cell, BarChart, ReferenceLine, Legend } from "recharts";
import { eur0, dfr } from "./forecastUtils";

const fmtAxis = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)} k€` : `${Math.round(v)} €`);

export function WaterfallChart({ items, total, height = 220, totalLabel = "Projection ajustée" }) {
  const data = useMemo(() => {
    let run = 0; const rows = [];
    items.forEach((w, i) => {
      if (i === 0 || w.key === "base" || w.key === "realise") { rows.push({ name: w.label, lo: run, v: w.value, tone: "base", val: w.value }); run += w.value; return; }
      if (Math.abs(w.value) < 0.5) return;
      const lo = w.value >= 0 ? run : run + w.value; rows.push({ name: w.label, lo, v: Math.abs(w.value), tone: w.value >= 0 ? "pos" : "neg", val: w.value }); run += w.value;
    });
    rows.push({ name: totalLabel, lo: 0, v: total, tone: "total", val: total });
    return rows.filter(r => !(r.tone === "base" && !r.v));
  }, [items, total, totalLabel]);
  const color = { base: "#94a3b8", pos: "#10b981", neg: "#f43f5e", total: "#2563eb", obj: "#7c3aed" };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={6}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-18} textAnchor="end" height={48} />
        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10 }} width={54} />
        <Tooltip formatter={(v, n, p) => [eur0(p.payload.val), p.payload.name]} labelFormatter={() => ""} />
        <Bar dataKey="lo" stackId="a" fill="transparent" isAnimationActive={false} />
        <Bar dataKey="v" stackId="a" radius={[4, 4, 0, 0]} isAnimationActive={false}>{data.map((r, i) => <Cell key={i} fill={color[r.tone]} />)}</Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function EvolutionChart({ byDay, objDays, height = 260, showObjective = true }) {
  const [mode, setMode] = useState("classic");
  const data = useMemo(() => {
    const om = Object.fromEntries((objDays || []).map(d => [d.date, d.objectif]));
    if (mode === "classic") {
      let cr = 0, cp = 0, cn = 0, co = 0, clo = 0, chi = 0;
      return byDay.map(r => {
        const p = r.projection || 0; cp += p; cn += r.n1_comparable || 0;
        if (r.done) { cr += r.realise || 0; clo = cp; chi = cp; } else { clo += p * 0.97; chi += p * 1.03; }
        co += om[r.date] ?? 0;
        return { d: dfr(r.date), realise: r.done ? cr : null, projection: cp, n1: cn, objectif: showObjective && objDays ? co : null, band: r.done ? [cp, cp] : [clo, chi] };
      });
    }
    const groups = new Map();
    byDay.forEach(r => {
      const dt = new Date(r.date + "T00:00:00");
      let k, lab;
      if (mode === "day") { k = r.date; lab = dfr(r.date); }
      else if (mode === "week") { const t = new Date(dt); t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7)); const y0 = new Date(t.getFullYear(), 0, 4); const w = 1 + Math.round(((t - y0) / 86400000 - 3 + ((y0.getDay() + 6) % 7)) / 7); k = `${t.getFullYear()}-W${String(w).padStart(2, "0")}`; lab = `S${String(w).padStart(2, "0")}`; }
      else { k = r.date.slice(0, 7); lab = dt.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }); }
      const g = groups.get(k) || { d: lab, realise: 0, projection: 0, n1: 0, objectif: 0, lo: 0, hi: 0, hasDone: false, hasTodo: false };
      const p = r.projection || 0; g.projection += p; g.n1 += r.n1_comparable || 0; g.objectif += om[r.date] ?? 0;
      if (r.done) { g.realise += r.realise || 0; g.hasDone = true; g.lo += p; g.hi += p; } else { g.hasTodo = true; g.lo += p * 0.97; g.hi += p * 1.03; }
      groups.set(k, g);
    });
    return [...groups.values()].map(g => ({ d: g.d, realise: g.hasDone ? g.realise : null, projection: g.projection, n1: g.n1, objectif: showObjective && objDays ? g.objectif : null, band: g.hasTodo ? [g.lo, g.hi] : [g.projection, g.projection] }));
  }, [byDay, objDays, showObjective, mode]);
  const cumul = mode === "classic";
  return (
    <div>
      <div className="flex justify-end gap-1 mb-1" data-testid="evo-view-modes">
        {[["classic", "Vue classique"], ["day", "Vue par jour"], ["week", "Vue par semaine"], ["month", "Vue par mois"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} data-testid={`evo-mode-${k}`} className={`text-[10px] px-2 py-0.5 rounded-md border ${mode === k ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="d" tick={{ fontSize: 9 }} interval={cumul || mode === "day" ? "preserveStartEnd" : 0} minTickGap={24} />
          <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10 }} width={54} />
          <Tooltip formatter={(v, n) => [Array.isArray(v) ? `${eur0(v[0])} → ${eur0(v[1])}` : eur0(v), n]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area type="monotone" dataKey="band" name="Fourchette" stroke="none" fill="#2563eb" fillOpacity={0.08} isAnimationActive={false} />
          <Bar dataKey="realise" name={cumul ? "CA réalisé (cumul)" : "CA réalisé"} fill="#2563eb" radius={[2, 2, 0, 0]} isAnimationActive={false} />
          {cumul ? <Line type="monotone" dataKey="projection" name="Projection Orbit" stroke="#2563eb" strokeDasharray="5 4" dot={false} strokeWidth={2} isAnimationActive={false} />
                 : <Bar dataKey="projection" name="Projection Orbit" fill="#93c5fd" radius={[2, 2, 0, 0]} isAnimationActive={false} />}
          <Line type="monotone" dataKey="n1" name="N-1 comparable" stroke="#94a3b8" dot={!cumul && mode !== "day"} strokeWidth={1.5} isAnimationActive={false} />
          {showObjective && objDays && <Line type="monotone" dataKey="objectif" name="Objectif simulé" stroke="#7c3aed" dot={!cumul && mode !== "day"} strokeWidth={2} isAnimationActive={false} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function VentilationBars({ subs, valueKey = "objectif", height = 200 }) {
  const data = subs.map(s => ({ name: s.label, v: s[valueKey] ?? s.projection, w: ((s.weight_obj ?? s.weight) * 100) }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10 }} width={54} />
        <Tooltip formatter={(v, n, p) => [`${eur0(v)} · ${p.payload.w.toFixed(1)} %`, "Objectif"]} />
        <Bar dataKey="v" fill="#7c3aed" radius={[6, 6, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 10, formatter: (v) => fmtAxis(v) }} />
        <ReferenceLine y={0} stroke="#cbd5e1" />
      </BarChart>
    </ResponsiveContainer>
  );
}
